// @flow

import React from "react";

import EditableForm from "../../components/EditableForm";
import FormRow from "../../components/FormRow";
import ConditionalSubform from "../../components/ConditionalSubform";
import EditableField from "../../components/EditableField";
import * as formConditions from "../../utils/formConditions";
import * as formatters from "../../utils/formatters";
import * as mappers from "../../utils/mappers";
import * as v from "../../utils/validators";
import { errors } from "../../utils/errors";
import { placeholders } from "../../utils/placeholders";

import {
  FINANCING_PRODUCT,
  LEASING_AMORTISATION_KIND,
  LEASING_AMORTISATION_KIND__PARTIAL_AMORTISATION,
  LEASING_AMORTISATION_KIND__HIRE_PURCHASE,
  LEASING_RESIDUAL_VALUE,
  LEASING_TERM_IN_MONTHS,
  LEASING_TAKEOVER,
  LEASING_DESCRIPTION,
  AMOUNT,
  FINANCING_OWN_FOUNDS,
  FINANCING_DEMAND,
  FINANCING_SUPPORT
} from "./formFields";
import { mapSupport, mapAmortisationKind, mapFinancingProduct } from "./mappers";

import { type FinancingSectionProps } from "../../types/details";
import { leasingTakeoverOptions } from "./options";

const FinancingLeasingEnterpriser = ({
  initialValues,
  handleSubmit,
  isLocked,
  site
}: FinancingSectionProps) => {
  return (
    <EditableForm initialValues={initialValues} onSubmit={handleSubmit} isLocked={isLocked}>
      <FormRow separator>
        <EditableField
          fieldControlProps={{
            name: FINANCING_SUPPORT,
            caption: () => "Unterstützung durch einen Experten"
          }}
          displayValue={mapSupport(site === "customer")}
        />
        <EditableField
          fieldControlProps={{
            name: FINANCING_PRODUCT,
            caption: () => "Finanzierungsart"
          }}
          displayValue={mapFinancingProduct}
        />
        <div />
      </FormRow>
      <FormRow separator>
        <EditableField
          fieldControlProps={{
            name: AMOUNT,
            caption: () => "Investitionskosten"
          }}
          displayValue={formatters.formatCurrency}
        />
        <EditableField
          fieldControlProps={{
            name: FINANCING_OWN_FOUNDS,
            caption: () => "Eigenmittel"
          }}
          displayValue={formatters.formatCurrency}
        />
        <EditableField
          fieldControlProps={{
            name: FINANCING_DEMAND,
            caption: () => "Finanzierungsbedarf"
          }}
          displayValue={formatters.formatCurrency}
        />
      </FormRow>
      <FormRow separator>
        <EditableField
          fieldControlProps={{
            name: LEASING_AMORTISATION_KIND,
            caption: () => "Vertragsart"
          }}
          displayValue={mapAmortisationKind}
        />
        <ConditionalSubform
          condition={formConditions.fieldHasValueIn(LEASING_AMORTISATION_KIND, [
            LEASING_AMORTISATION_KIND__PARTIAL_AMORTISATION,
            LEASING_AMORTISATION_KIND__HIRE_PURCHASE
          ])}
          renderOnSatisfy={() => (
            <EditableField
              fieldControlProps={{
                name: LEASING_RESIDUAL_VALUE,
                caption: () => "Restwert / Blockrate"
              }}
              displayValue={formatters.formatPercent}
            />
          )}
          renderOnFail={() => <div />}
        />
      </FormRow>
      <FormRow>
        <EditableField
          fieldControlProps={{
            name: LEASING_TERM_IN_MONTHS,
            caption: () => "Laufzeit",
            validate: v.combineWithPrefillCheck(errors.requiredField())
          }}
          displayValue={formatters.formatMonths}
        />
        <EditableField
          fieldControlProps={{
            name: LEASING_TAKEOVER,
            caption: () => "Wie soll am Ende der Laufzeit mit dem Objekt verfahren werden?",
            validate: v.combineWithPrefillCheck(errors.requiredField())
          }}
          type="select"
          displayValue={mappers.optionsMapper}
          inputProps={{
            options: leasingTakeoverOptions,
            placeholder: placeholders.pleaseSelect
          }}
        />
        <EditableField
          fieldControlProps={{
            name: LEASING_DESCRIPTION,
            caption: () => "Weitere Vorgaben zum Leasing",
            validate: v.combineWithPrefillCheck(
              errors.requiredField(),
              v.maxLength(500, errors.maxLengthCharacters(500))
            )
          }}
          type="textArea"
          inputProps={{
            placeholder:
              "Beispielsweise Verlängerungsoptionen am Ende der Grundmietzeit gewünscht..."
          }}
        />
        <div />
      </FormRow>
    </EditableForm>
  );
};

export default FinancingLeasingEnterpriser;
